import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Plus, Trash2, MessageSquare, Library as LibraryIcon, Save } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Good luck to everyone in the {event_name}!",
  "Proud to support the {event_name}.",
  "Cheering on all the participants at {event_name}.",
  "Best wishes from your friends at {company}.",
  "Have a great {event_name} — see you at the next one!",
];

/**
 * Lets the user define journal-wide caption fallback templates + variables.
 * Tokens: {title}, {company}, {index}, {n}, and any user-defined variable key.
 */
export default function CaptionFallbackInspector({ captionPrefs, setCaptionPrefs, journalId }) {
  const {
    caption_fallback_enabled: enabled = false,
    caption_templates: templates = [],
    caption_variables: variables = {},
  } = captionPrefs;

  const update = (patch) => setCaptionPrefs({ ...captionPrefs, ...patch });

  const addTemplate = (text = "") => {
    update({ caption_templates: [...(templates || []), text] });
  };
  const setTemplate = (i, v) => {
    const next = [...templates];
    next[i] = v;
    update({ caption_templates: next });
  };
  const removeTemplate = (i) => {
    update({ caption_templates: templates.filter((_, j) => j !== i) });
  };

  const setVar = (key, value) => {
    const next = { ...variables, [key]: value };
    update({ caption_variables: next });
  };
  const renameVar = (oldKey, newKey) => {
    if (oldKey === newKey || !newKey.trim()) return;
    const next = { ...variables };
    next[newKey] = next[oldKey];
    delete next[oldKey];
    update({ caption_variables: next });
  };
  const removeVar = (key) => {
    const next = { ...variables };
    delete next[key];
    update({ caption_variables: next });
  };
  const addVar = () => {
    let key = "event_name";
    let i = 2;
    while (Object.prototype.hasOwnProperty.call(variables, key)) {
      key = `event_name_${i++}`;
    }
    update({ caption_variables: { ...variables, [key]: "" } });
  };

  const varEntries = Object.entries(variables);

  const [libraries, setLibraries] = useState([]);
  const [importSelected, setImportSelected] = useState("");
  const [saveDlgOpen, setSaveDlgOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [savingLib, setSavingLib] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    api.get("/caption-libraries")
      .then(({ data }) => setLibraries(data))
      .catch(() => {});
  }, [enabled]);

  const importLibrary = (libId) => {
    const lib = libraries.find((l) => l.id === libId);
    if (!lib) return;
    const mergedVars = { ...(lib.variables || {}), ...variables };
    update({
      caption_templates: [...(templates || []), ...(lib.templates || [])].filter(Boolean),
      caption_variables: mergedVars,
    });
    toast.success(`Imported ${lib.templates?.length || 0} template(s) from ${lib.name}`);
    // Reset so the same library can be re-imported
    setTimeout(() => setImportSelected(""), 0);
  };

  const saveAsLibrary = async (e) => {
    e?.preventDefault?.();
    if (!journalId) return toast.error("Save the journal first");
    if (!saveName.trim()) return toast.error("Name is required");
    setSavingLib(true);
    try {
      const { data } = await api.post(`/journals/${journalId}/save-caption-library`, {
        name: saveName.trim(),
        description: "",
      });
      setLibraries((arr) => [...arr, data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success(`Saved as “${data.name}”`);
      setSaveDlgOpen(false);
      setSaveName("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingLib(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-zinc-800 pt-4">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow flex items-center gap-1">
          <MessageSquare className="w-3 h-3" /> Caption fallbacks
        </div>
        <Switch
          data-testid="caption-fallback-toggle"
          checked={!!enabled}
          onCheckedChange={(v) => update({ caption_fallback_enabled: v })}
        />
      </div>
      <p className="text-[10px] text-zinc-500 -mt-1">
        When a slide’s caption is empty, the renderer picks the next template (round-robin) and
        substitutes <span className="font-mono">{"{var}"}</span> tokens. Built-ins: <span className="font-mono">{"{title}"}</span>, <span className="font-mono">{"{company}"}</span>, <span className="font-mono">{"{n}"}</span>.
      </p>

      {enabled && (
        <>
          <div className="flex items-center gap-2 -mt-1 flex-wrap">
            {libraries.length > 0 && (
              <Select value={importSelected} onValueChange={importLibrary}>
                <SelectTrigger
                  data-testid="import-library-select"
                  className="bg-zinc-950 border-zinc-800 h-7 text-xs flex-1 min-w-0"
                >
                  <SelectValue placeholder="Import library…" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-72">
                  {libraries.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      <span className="inline-flex items-center gap-2">
                        <LibraryIcon className="w-3 h-3 text-blue-400" />
                        {l.name}
                        <span className="text-zinc-500 text-[10px]">
                          ({l.templates?.length || 0})
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {(templates?.length > 0 || varEntries.length > 0) && journalId && (
              <button
                type="button"
                data-testid="save-as-library-btn"
                onClick={() => setSaveDlgOpen(true)}
                className="text-[11px] text-blue-300 hover:text-blue-200 inline-flex items-center gap-1 whitespace-nowrap"
              >
                <Save className="w-3 h-3" /> Save as library
              </button>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="label-eyebrow">Variables</Label>
              <button
                type="button"
                data-testid="add-caption-var"
                onClick={addVar}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                + Add
              </button>
            </div>
            {varEntries.length === 0 && (
              <p className="text-[10px] text-zinc-500">No variables yet — add one like <span className="font-mono">event_name</span>.</p>
            )}
            {varEntries.map(([k, v]) => (
              <div key={k} className="flex items-center gap-1.5">
                <Input
                  data-testid={`caption-var-key-${k}`}
                  defaultValue={k}
                  onBlur={(e) => renameVar(k, e.target.value.trim())}
                  placeholder="key"
                  className="bg-zinc-950 border-zinc-800 h-7 text-xs font-mono w-28"
                />
                <Input
                  data-testid={`caption-var-val-${k}`}
                  value={v || ""}
                  onChange={(e) => setVar(k, e.target.value)}
                  placeholder="value"
                  className="bg-zinc-950 border-zinc-800 h-7 text-xs flex-1"
                />
                <button
                  type="button"
                  data-testid={`remove-caption-var-${k}`}
                  onClick={() => removeVar(k)}
                  className="text-zinc-500 hover:text-red-400 p-1"
                  aria-label={`Remove ${k}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="label-eyebrow">Templates ({templates.length})</Label>
              <button
                type="button"
                data-testid="add-caption-template"
                onClick={() => addTemplate("")}
                className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-0.5"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {templates.length === 0 && (
              <div className="space-y-1">
                <p className="text-[10px] text-zinc-500">Pick a starter:</p>
                <div className="flex flex-wrap gap-1">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`suggest-template-${i}`}
                      onClick={() => addTemplate(s)}
                      className="text-[10px] text-zinc-300 hover:text-zinc-50 bg-zinc-950 border border-zinc-800 hover:border-zinc-700 rounded px-2 py-1 text-left"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {templates.map((t, i) => (
              <div key={i} className="flex items-start gap-1.5">
                <span className="text-[10px] text-zinc-600 font-mono pt-1 w-4 text-right">{i + 1}.</span>
                <Textarea
                  data-testid={`caption-template-${i}`}
                  value={t}
                  onChange={(e) => setTemplate(i, e.target.value)}
                  rows={2}
                  placeholder="e.g. Good luck to everyone in the {event_name}!"
                  className="bg-zinc-950 border-zinc-800 text-xs flex-1 min-h-0 py-1"
                />
                <button
                  type="button"
                  data-testid={`remove-caption-template-${i}`}
                  onClick={() => removeTemplate(i)}
                  className="text-zinc-500 hover:text-red-400 p-1 mt-1"
                  aria-label={`Remove template ${i + 1}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            {templates.length > 0 && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                data-testid="add-empty-template"
                onClick={() => addTemplate("")}
                className="text-xs text-zinc-400 hover:text-zinc-100 h-7"
              >
                <Plus className="w-3 h-3 mr-1" /> Add another
              </Button>
            )}
          </div>
        </>
      )}

      <Dialog open={saveDlgOpen} onOpenChange={setSaveDlgOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Save as library</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Snapshot this journal’s {templates?.length || 0} template
              {(templates?.length || 0) === 1 ? "" : "s"} into a reusable library.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveAsLibrary} className="space-y-3">
            <div className="space-y-1">
              <Label className="label-eyebrow">Library name</Label>
              <Input
                data-testid="save-library-name"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g. Spring tournament messages"
                autoFocus
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                data-testid="save-library-submit"
                disabled={savingLib}
                className="bg-blue-600 hover:bg-blue-500"
              >
                <Save className="w-4 h-4 mr-2" /> {savingLib ? "Saving…" : "Save library"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
